import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/users/profileService';
import { UserRole } from '../../types/database';
import { dataRightsService } from '../../services/compliance/dataRightsService';
import AnonymizationSection from '../data-rights/AnonymizationSection';

interface ParentRouteProps {
  children: React.ReactNode;
}

const ParentRoute: React.FC<ParentRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [pendingDeletion, setPendingDeletion] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setAllowed(false);
        setPendingDeletion(false);
        return;
      }

      const profile = await profileService.getProfile(user.id);
      setAllowed(profile?.user_role === UserRole.Parent);
      const deletionStatus = await dataRightsService.getStatus();
      setPendingDeletion(Boolean(deletionStatus.data?.pendingAnonymization));
    };

    if (!loading) {
      checkAccess();
    }
  }, [user, loading]);

  if (loading || allowed === null || pendingDeletion === null) {
    return null;
  }

  if (!allowed) {
    return <Navigate to="/home" replace state={{ from: location }} />;
  }

  if (pendingDeletion) {
    return (
      <main className="mx-auto max-w-2xl p-6">
        <h1 className="mb-2 text-2xl font-semibold text-rowing-navy">Account deletion scheduled</h1>
        <p className="mb-6 text-gray-700">
          Your account is locked while deletion is pending. Cancel the request below to restore
          access.
        </p>
        <AnonymizationSection persona="parent" />
      </main>
    );
  }

  return <>{children}</>;
};

export default ParentRoute;
